import React, { useState, useEffect } from "react";

import {
  TextField,
  Alert,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  Select as MSelect,
  MenuItem,
} from "@mui/material";
import { Update, Delete } from "@mui/icons-material";
import Select from "react-select";
import apiUrl from "../api";

import axios from "axios";

const DeleteDialog = (props) => {
  const [selectedPool, setSelectedPool] = useState("");

  const handlePoolDelete = async (e) => {
    e.preventDefault();
    props.setError("");
    props.setSuccess("");

    try {
      // Create request data object with replacement pool only if one is selected
      const requestData = {};
      if (selectedPool) {
        requestData.replacePool = selectedPool;
      }

      const response = await axios.delete(
        `${apiUrl}/pool-options/deletePoolOption/${props.selectedPool}`,
        {
          data: requestData,
          headers: {
            Authorization: `Bearer ${props.user.token}`,
          },
        }
      );

      props.setPools(
        props.pools.filter((pool) => pool._id !== props.selectedPool)
      );

      props.setSuccess("Pool option deleted successfully.");
      props.onClose();
    } catch (error) {
      console.log(error);
      props.setError("Failed to delete pool option. Please try again.");
      props.onClose();
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Delete Pool Option"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to delete this pool option? You can optionally select another pool option to replace it.
        </DialogContentText>

        {props.pools.filter((pool) => pool._id !== props.selectedPool).length > 0 && (
          <>
            <DialogContentText 
              style={{ marginTop: '16px', fontSize: '0.9rem', color: '#666' }}
            >
              Optional: Select a replacement pool option
            </DialogContentText>
            
            <MSelect
              value={selectedPool}
              onChange={(e) => setSelectedPool(e.target.value)}
              fullWidth
              margin="normal"
              displayEmpty
            >
              <MenuItem value="">
                <em>No replacement</em>
              </MenuItem>
              {props.pools
                .filter((pool) => pool._id !== props.selectedPool)
                .map((pool) => (
                  <MenuItem key={pool._id} value={pool._id}>
                    {pool.title}
                  </MenuItem>
                ))}
            </MSelect>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button onClick={handlePoolDelete} color="error" autoFocus>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UpdatePoolOption = () => {
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState("");
  const [poolName, setPoolName] = useState("");
  const [poolPrice, setPoolPrice] = useState(0); // Default price is 0
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchPools = async () => {
      try {
        const response = await axios.get(`${apiUrl}/pool-options/getPoolOptions`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        setPools(response.data);
      } catch (error) {
        console.error("Error fetching pool options:", error);
        setError("Failed to fetch pool options. Please try again.");
      }
    };

    fetchPools();
  }, [user.token]);

  const handlePoolChange = (selectedOption) => {
    setSelectedPool(selectedOption.value);
    const pool = pools.find((pool) => pool._id === selectedOption.value);
    setPoolName(pool.title);
    setPoolPrice(pool.price || 0); // Set price from selected pool, default to 0 if not available
  };

  const handleUpdatePool = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedPool) {
      return setError("Please select a pool option to update.");
    }

    if (poolName === "") {
      return setError("Pool name is required.");
    }

    try {
      const response = await axios.put(
        `${apiUrl}/pool-options/updatePoolOption/${selectedPool}`,
        {
          title: poolName,
          price: poolPrice
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      // Update the pools state with the updated pool
      setPools(
        pools.map((pool) =>
          pool._id === selectedPool
            ? { ...pool, title: poolName, price: poolPrice }
            : pool
        )
      );

      setSuccess("Pool option updated successfully.");
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setError("Pool option with this name already exists.");
      } else {
        setError("Failed to update pool option. Please try again.");
      }
    }
  };

  const handleOpenDeleteDialog = () => {
    if (!selectedPool) {
      return setError("Please select a pool option to delete.");
    }
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const poolOptions = pools.map((pool) => ({
    value: pool._id,
    label: pool.title,
  }));

  return (
    <div className="update-pool-option-wrapper">
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      <h4
        style={{
          marginBottom: "0px",
          fontWeight: "600",
        }}
      >
        Update Pool Option
      </h4>
      <form
        onSubmit={handleUpdatePool}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            width: "100%",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            label="Pool Option Name"
            placeholder="Pool Option Name"
            value={poolName}
            onChange={(e) => setPoolName(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              inputProps: { style: { height: "56px", padding: "5px" } },
            }}
          />
          <TextField
            variant="outlined"
            type="number"
            label="Price"
            placeholder="Price"
            value={poolPrice}
            onChange={(e) => setPoolPrice(Number(e.target.value))}
            margin="normal"
            sx={{
              width: "20%",
            }}
            InputProps={{
              inputProps: { 
                style: { height: "56px", padding: "5px" },
                min: 0,
                step: "0.01"
              },
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            width: "100%",
            marginBottom: "10px"
          }}
        >
          <div style={{ width: "100%" }}>
            <Select
              options={poolOptions}
              onChange={handlePoolChange}
              placeholder="Select Pool Option"
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: "56px",
                }),
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            width: "100%",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={handleOpenDeleteDialog}
            style={{
              backgroundColor: "#d32f2f",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "56px",
            }}
          >
            <Delete />
            Delete
          </button>
          <button
            type="submit"
            style={{
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "56px",
            }}
          >
            <Update />
            Update
          </button>
        </div>
      </form>

      <DeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        selectedPool={selectedPool}
        pools={pools}
        setPools={setPools}
        setError={setError}
        setSuccess={setSuccess}
        user={user}
      />
    </div>
  );
};

export default UpdatePoolOption;